import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { 
  Bell,
  BellRinging,
  BellSlash,
  SpinnerGap,
  CheckCircle,
  Warning
} from '@phosphor-icons/react';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PushNotificationSettings = () => {
  const [subscribed, setSubscribed] = useState(false);
  const [preferences, setPreferences] = useState({
    daily_reminder: true,
    reminder_time: '09:00',
    streak_notifications: true
  });
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [testSending, setTestSending] = useState(false);
  const [error, setError] = useState(null);
  const [vapidKey, setVapidKey] = useState(null);
  const [supported, setSupported] = useState(true);

  useEffect(() => {
    checkSupport();
    fetchStatus();
  }, []);

  const checkSupport = () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setSupported(false);
      setLoading(false);
      return false;
    }
    return true;
  };

  const fetchStatus = async () => {
    try {
      const [statusRes, prefsRes, keyRes] = await Promise.all([
        axios.get(`${API}/push/status`, { withCredentials: true }),
        axios.get(`${API}/notifications/preferences`, { withCredentials: true }),
        axios.get(`${API}/push/vapid-key`, { withCredentials: true })
      ]);
      
      setSubscribed(statusRes.data.subscribed);
      setPreferences(prefsRes.data);
      setVapidKey(keyRes.data.publicKey);
    } catch (err) {
      console.error('Failed to fetch push status:', err);
    } finally {
      setLoading(false);
    }
  };

  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const handleSubscribe = async () => {
    if (!supported || !vapidKey) return;
    
    setSubscribing(true);
    setError(null);
    
    try {
      // Request notification permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setError('Notification permission denied');
        setSubscribing(false);
        return;
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;
      
      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey)
      });

      // Send subscription to server
      const subJson = subscription.toJSON();
      await axios.post(`${API}/push/subscribe`, {
        endpoint: subJson.endpoint,
        keys: subJson.keys
      }, { withCredentials: true });

      setSubscribed(true);
    } catch (err) {
      console.error('Subscription failed:', err);
      setError(err.message || 'Failed to subscribe');
    } finally {
      setSubscribing(false);
    }
  };

  const handleUnsubscribe = async () => {
    setSubscribing(true);
    setError(null);
    
    try {
      // Unsubscribe from server
      await axios.post(`${API}/push/unsubscribe`, {}, { withCredentials: true });
      
      // Also unsubscribe from browser
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await subscription.unsubscribe();
      }
      
      setSubscribed(false);
    } catch (err) {
      console.error('Unsubscribe failed:', err);
      setError(err.message || 'Failed to unsubscribe');
    } finally {
      setSubscribing(false);
    }
  };

  const handleTestNotification = async () => {
    setTestSending(true);
    setError(null);
    
    try {
      await axios.post(`${API}/push/test`, {}, { withCredentials: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send test notification');
    } finally {
      setTestSending(false);
    }
  };

  const updatePreferences = async (updates) => {
    const newPrefs = { ...preferences, ...updates };
    setPreferences(newPrefs);
    
    try {
      await axios.put(`${API}/notifications/preferences`, newPrefs, { withCredentials: true });
    } catch (err) {
      console.error('Failed to update preferences:', err);
    }
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-sm p-6">
        <div className="flex items-center justify-center py-4">
          <SpinnerGap className="w-6 h-6 text-primary animate-spin" />
        </div>
      </div>
    );
  }

  if (!supported) {
    return (
      <div className="bg-card border border-border rounded-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <BellSlash weight="fill" className="w-5 h-5 text-muted-foreground" />
          <h2 className="font-heading font-bold text-lg">Push Notifications</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          Push notifications are not supported in this browser.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-sm p-6" data-testid="push-settings">
      <div className="flex items-center gap-3 mb-4">
        <BellRinging weight="fill" className={cn("w-5 h-5", subscribed ? "text-accent" : "text-muted-foreground")} />
        <h2 className="font-heading font-bold text-lg">Push Notifications</h2>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 mb-4 bg-destructive/10 border border-destructive/20 rounded-sm text-destructive text-sm">
          <Warning weight="fill" className="w-4 h-4" />
          {error}
        </div>
      )}

      {/* Subscribe/Unsubscribe */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <Label className="font-medium">Daily Reminders</Label>
          <p className="text-xs text-muted-foreground">
            {subscribed ? 'Notifications enabled' : 'Get reminded to study every day'}
          </p>
        </div>
        {subscribed ? (
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleUnsubscribe}
            disabled={subscribing}
            className="rounded-sm"
          >
            {subscribing ? <SpinnerGap className="w-4 h-4 animate-spin" /> : 'Disable'}
          </Button>
        ) : (
          <Button 
            size="sm"
            onClick={handleSubscribe}
            disabled={subscribing}
            className="rounded-sm"
          >
            {subscribing ? <SpinnerGap className="w-4 h-4 animate-spin" /> : 'Enable'}
          </Button>
        )}
      </div>

      {subscribed && (
        <>
          {/* Preferences */}
          <div className="space-y-4 mb-6 pt-4 border-t border-border">
            <div className="flex items-center justify-between">
              <div>
                <Label className="font-medium">Daily Reminder</Label>
                <p className="text-xs text-muted-foreground">Remind me to study each day</p>
              </div>
              <Switch
                checked={preferences.daily_reminder}
                onCheckedChange={(checked) => updatePreferences({ daily_reminder: checked })}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label className="font-medium">Streak Alerts</Label>
                <p className="text-xs text-muted-foreground">Alert when my streak is at risk</p>
              </div>
              <Switch
                checked={preferences.streak_notifications}
                onCheckedChange={(checked) => updatePreferences({ streak_notifications: checked })}
              />
            </div>

            {preferences.daily_reminder && (
              <div className="flex items-center justify-between">
                <div>
                  <Label className="font-medium">Reminder Time</Label>
                  <p className="text-xs text-muted-foreground">When should we remind you?</p>
                </div>
                <select
                  value={preferences.reminder_time}
                  onChange={(e) => updatePreferences({ reminder_time: e.target.value })}
                  className="px-3 py-1.5 bg-background border border-input rounded-sm text-sm"
                >
                  <option value="07:00">7:00 AM</option>
                  <option value="08:00">8:00 AM</option>
                  <option value="09:00">9:00 AM</option>
                  <option value="12:00">12:00 PM</option>
                  <option value="18:00">6:00 PM</option>
                  <option value="20:00">8:00 PM</option>
                  <option value="21:00">9:00 PM</option>
                </select>
              </div>
            )}
          </div>

          {/* Test Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleTestNotification}
            disabled={testSending}
            className="w-full rounded-sm"
            data-testid="test-notification-btn"
          >
            {testSending ? (
              <SpinnerGap className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Bell className="w-4 h-4 mr-2" />
            )}
            Send Test Notification
          </Button>
        </>
      )}
    </div>
  );
};
